// SceneFormFile.js
import React, { useCallback, useState, useEffect } from "react";
import { Modal, Form, Button, Image } from "semantic-ui-react";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import { initialValues, validationSchema } from "./SceneFileform.form";
import { VirtualScenes } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useTranslation } from "react-i18next";
import "./SceneFormFile.scss";

const virtualScenesController = new VirtualScenes();

export function SceneFormFile({ isOpen, onClose, onSubmit, isPublic }) {
  const { accessToken } = useAuth();
  const { t } = useTranslation("scenes");
  const [previewImage, setPreviewImage] = useState(null); // Previsualización de imágenes

  const formik = useFormik({
    initialValues: {
      ...initialValues(),
      isPublic: isPublic,
    },
    enableReinitialize: true,
    validationSchema: validationSchema(),
    onSubmit: async (formData) => {
      if (!formData.file) {
        console.error("No hay archivo seleccionado");
        return;
      }

      try {
        let response;
        if (isPublic) {
          // Subir imagen
          response = await virtualScenesController.uploadImageFile(
            accessToken,
            formData,
          );
        } else {
          // Subir archivo comprimido
          response = await virtualScenesController.uploadRepoFile(
            accessToken,
            formData,
          );
        }

        // Pasar los datos de la imagen o archivo subido al componente padre
        onSubmit({
          id: response.id,
          url: formData.isPublic ? URL.createObjectURL(formData.file) : null,
          title: formData.file.name,
        });

        onClose();
      } catch (error) {
        console.error("Error subiendo archivo:", error);
      }
    },
  });

  const { resetForm } = formik; // Extrae resetForm

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setPreviewImage(null);
    }
  }, [isOpen, resetForm]); //jecuta cuando el estado de isOpen cambia

  // Manejador de Dropzone
  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      formik.setFieldValue("file", file);
      if (isPublic) {
        setPreviewImage(URL.createObjectURL(file)); // Genera previsualización para imágenes
      }
    },
    [formik, isPublic],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false, // Solo un archivo a la vez
  });

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header>
        {isPublic ? t("form.uploadImageTitle") : t("form.uploadFileTitle")}
      </Modal.Header>
      <Modal.Content>
        <Form onSubmit={formik.handleSubmit}>
          <Form.Input
            name="idScene"
            placeholder={
              isPublic
                ? t("form.imageIdPlaceholder")
                : t("form.fileIdPlaceholder")
            }
            onChange={formik.handleChange}
            value={formik.values.idScene}
            error={formik.errors.idScene ? true : false}
          />
          <Form.Input
            name="title"
            placeholder={
              isPublic
                ? t("form.imageTitlePlaceholder")
                : t("form.fileTitlePlaceholder")
            }
            onChange={formik.handleChange}
            value={formik.values.title}
            error={formik.errors.title ? true : false}
          />
          <Form.TextArea
            name="description"
            placeholder={
              isPublic
                ? t("form.imageDescPlaceholder")
                : t("form.fileDescPlaceholder")
            }
            onChange={formik.handleChange}
            value={formik.values.description}
            error={formik.errors.description ? true : false}
          />

          <div {...getRootProps()} className="dropzone">
            <input {...getInputProps()} />
            <Button type="button">
              {isPublic ? t("form.selectImageFile") : t("form.selectZipFile")}
            </Button>
          </div>

          {isPublic && previewImage && (
            <div className="image-preview">
              <Image src={previewImage} size="small" centered />
            </div>
          )}

          <Form.Button
            type="submit"
            primary
            fluid
            loading={formik.isSubmitting}
          >
            {isPublic ? t("form.uploadButton") : t("form.uploadFileButton")}
          </Form.Button>
        </Form>
      </Modal.Content>
    </Modal>
  );
}
